import React, { useEffect, useRef } from 'react'
import { ViewportPortal, useStore } from '@xyflow/react'
import type { CanvasNote } from '@/src/renderer/lib/db/models'
import { getCanvasNoteRingColor, type CanvasNoteColorKey } from './utils/canvasNoteStyle'
import { CanvasOptionsBar, type CanvasOptionsBarButton } from './optionsBar'
import { useCanvasPointerBusy } from './hooks/useCanvasOptionsBar'
import { usePresence } from './hooks/usePresence'
import { getBoundingBox } from './utils/canvasAlignment'
import { canvasCounterScale, selectionStrokeWidth } from './utils/canvasViewport'
import type { Bounds, MovingRect, ResizeCorner } from './utils/canvasAlignment'
import { SWAP_RING_LIFTED_Z } from './utils/canvasSwap'
import type { SwapEntry } from './utils/canvasSwap'
import type { SwapZone } from './utils/canvasSwapZones'
import type { SeamMark } from './utils/canvasShove'
import './SelectionOverlay.scss'
import { isConvertible, isConverted } from './utils/noteToTask'
import { canvasTextSizeOf } from './utils/canvasText'
import type { CanvasColorPreview, CanvasFontPreview, CanvasOptionsBarApplyState } from './optionsBar/CanvasOptionsBar.types'

/** Die vier Ecken, an denen die Gruppe skaliert wird. */
const CORNERS: readonly ResizeCorner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right']

/**
 * Die **Fuge** — die Marke des **Dazwischen schiebens** (CONTEXT.md): eine
 * LINIE mittig im Spalt, der aufgeht. Sie unterscheidet die Geste vom **Platz
 * tauschen**, das mit demselben Zug ebenfalls fremde Zettel bewegt; dort ist
 * die Marke der **Umriss** um den Partner, und den zeichnet der Zettel selbst.
 *
 * Sie trägt den Ring-Ton des GETRAGENEN Zettels, nicht das Blau der
 * **Hilfslinien**: Die erscheinen in derselben Geste, sobald sie jenseits des
 * Feld-Rings frei trägt, und eine blaue Linie in einer Lücke wäre von einer
 * Hilfslinie nicht zu unterscheiden.
 */
export interface SwapSeam {
  seam: SeamMark
  accent: string
  /**
   * Ob sie STEHT. Beim Abblenden bleibt sie noch einen Moment mit `false`
   * gesetzt — verschwände sie einfach, gäbe es nichts, woran eine Blende
   * ansetzen könnte (siehe `showSeam` in `CanvasFlow`).
   */
  on: boolean
}


export interface SelectionOverlayProps {
  /** Die ausgewählten Notizen samt Kennung. Ab zwei erscheint der Rahmen. */
  entries: SwapEntry[]
  /**
   * Der Rahmen, wie er beim Anfassen eines **Tauschpunkts** war — solange
   * gesetzt, ersetzt er das laufend gerechnete Rechteck. Siehe unten.
   */
  frozenBox?: Bounds | null
  /** Ziehen an einer Ecke — skaliert die ganze Gruppe. */
  onResizeStart: (corner: ResizeCorner, event: React.PointerEvent) => void
  /** Ziehen an der Fläche dazwischen — verschiebt die ganze Gruppe. */
  onMoveStart: (event: React.PointerEvent) => void
  /** Ziehen an einem **Tauschpunkt** — tauscht den Platz mit einem anderen. */
  onSwapStart: (id: string, event: React.PointerEvent) => void
  /**
   * Das gerade gezogene Element, falls eines getauscht wird.
   *
   * Das ZIEL bekommt nichts: Bis September 2026 lieh ihm der Overlay einen
   * gefüllten Ring („hier tauschst du hin"). Der sah aus, als würde er
   * gedrückt — ein Griff also, an einem Zettel, den man gar nicht angefasst
   * hat. Was geschieht, zeigt der Partner ohnehin, indem er auf den Platz des
   * Getragenen gleitet.
   */
  swapDraggedId?: string | null
  /**
   * Die **Ableg-Flächen** des Zettels nahe dem Zeiger — davor / auf /
   * dahinter — und welche davon getroffen ist (gehoben). Gerechnet in
   * `useSwapDrag`; hier nur gezeichnet, ohne Zeiger.
   */
  swapZones?: SwapZone[]
  swapHit?: SwapZone | null
  /**
   * Die **Zielfläche**: wo der Getragene beim Loslassen liegt. `null` (und
   * damit unsichtbar) im freien **Verschieben** — dort steht er selbst schon
   * dort.
   */
  swapLanding?: MovingRect | null
  /**
   * Die **Fuge** der laufenden Geste. `null`, wo nichts eingereiht wird: beim
   * Tausch (dort trägt der Partner den **Umriss**), über einem **Leerplatz**,
   * im freien Tragen, und solange die Ruhe vor dem Angebot läuft.
   */
  swapSeam?: SwapSeam | null
  /**
   * Doppelklick auf einen **Tauschpunkt**. Er liegt mitten auf dem Zettel
   * und darf ihm den Doppelklick zum Bearbeiten nicht stehlen.
   */
  onSwapPointDoubleClick?: (id: string) => void
  /**
   * Der Zeiger kommt auf einen **Tauschpunkt**. Sein Zettel **wächst**
   * daraufhin ein wenig (CONTEXT.md, Tauschpunkt) — die Ansage, dass dieser
   * Griff zu ihm gehört und was gleich in der Hand liegt.
   *
   * Über einen Rückruf und nicht per CSS: Der Ring liegt im `ViewportPortal`,
   * der Zettel im Node-Baum — vom einen zum anderen führt kein Selektor.
   */
  onSwapPointEnter?: (id: string) => void
  /**
   * Der Zeiger verlässt einen **Tauschpunkt**. Der Ring liegt über dem
   * Zettel, im Portal — springt der Zeiger von ihm direkt ins Leere, hat der
   * Zettel sein Verlassen längst gemeldet (und `CanvasFlow` es ignoriert,
   * weil es auf den Ring ging). Jetzt muss der Ring es tun.
   */
  onSwapPointLeave?: (id: string, event: React.PointerEvent) => void
  /**
   * Der Zettel, der gerade bearbeitet wird — sein Tauschpunkt weicht. Beim
   * Tippen läge sonst ein Greif-Ring über dem Text.
   */
  editingId?: string | null
  /**
   * Wer einen **Tauschpunkt** trägt. Ohne Angabe: die Auswahl. `CanvasFlow`
   * gibt hier den überfahrenen Zettel, das getragene Element und — ab zwei —
   * die Auswahl herein (CONTEXT.md, „Tauschpunkt").
   */
  swapPoints?: SwapEntry[]
  /**
   * Die ausgewählten Elemente — Grundlage der gemeinsamen **Options-Leiste**.
   *
   * Ohne sie erscheint keine Leiste: Die Geschichten und Tests, die nur den
   * Rahmen prüfen, sollen sie nicht mitstellen müssen.
   */
  notes?: readonly CanvasNote[]
  /** Setzt die Farbe auf ALLE ausgewählten — als EIN Schritt. */
  onBulkColor?: (key: CanvasNoteColorKey) => void
  /** Setzt die Schrift auf alle ausgewählten — als ein Schritt. */
  onBulkFont?: (key: string | null) => void
  /** Schaltet den Titel aller ausgewählten **Notizen** um — als ein Schritt. */
  onBulkToggleTitle?: () => void
  /** Schaltet den Autor aller ausgewählten um — als ein Schritt. */
  onBulkToggleAuthor?: () => void
  /** Löscht alle ausgewählten — als ein Schritt. */
  onBulkDelete?: () => void
  /** Die **Übernehmen-Pille** der Gruppen-Leiste: alle übernahmefähigen der Auswahl. */
  onBulkApply?: () => void
  /**
   * **Ankleben** für alle ausgewählten — als EIN Schritt, damit ein ⌘Z sie
   * alle wieder ablöst.
   *
   * Es gibt kein Gegenstück: Ein geklebter Zettel gerät in keine Auswahl,
   * **Ablösen** geht also nur einzeln über seinen Streifen.
   */
  onBulkTape?: () => void
  /** Setzt die Schriftgröße aller ausgewählten **Texte** — als ein Schritt. */
  onBulkFontSize?: (size: number) => void
  /** Setzt die **Standard**-Tinte auf alle ausgewählten Texte — als ein Schritt. */
  onBulkColorReset?: () => void
  /** Die Vorschau beim Überfahren der Farben — schreibt nicht (siehe `CanvasColorPreview`). */
  onBulkColorPreview?: (preview: CanvasColorPreview) => void
  /** Die Vorschau beim Überfahren des Schriftmenüs — schreibt nicht. */
  onBulkFontPreview?: (preview: CanvasFontPreview) => void
}

/**
 * Der gemeinsame Rahmen um mehrere ausgewählte **Notizen**, mit Griffen zum
 * Skalieren und je einem **Tauschpunkt** in der Mitte — wie in FigJam und
 * Miro.
 *
 * **Warum nicht React Flows `NodesSelection`:** Das erscheint nur nach einem
 * Auswahlrahmen (`nodesSelectionActive` wird allein im `onPointerUp` der Pane
 * gesetzt), nicht nach ⇧-Klick; jeder Klick auf eine Notiz schaltet es ab
 * (`handleNodeClick` setzt das Flag unbedingt zurück); und Griffe hat es
 * keine. Dieses Overlay hängt stattdessen an der Auswahl selbst.
 *
 * Gerendert im `ViewportPortal` wie `HelperLines` und `MeasureLines` — dort
 * gelten Canvas-Koordinaten, der Rahmen wird also mitverschoben und -gezoomt.
 *
 * @example
 * <SelectionOverlay entries={selectedRects} onResizeStart={…} onMoveStart={…} onSwapStart={…} />
 */
export const SelectionOverlay: React.FC<SelectionOverlayProps> = ({
  entries,
  frozenBox,
  onResizeStart,
  onMoveStart,
  onSwapStart,
  swapDraggedId,
  swapZones,
  swapHit,
  swapLanding,
  swapSeam,
  onSwapPointDoubleClick,
  onSwapPointEnter,
  onSwapPointLeave,
  editingId,
  swapPoints,
  notes,
  onBulkColor,
  onBulkFont,
  onBulkToggleTitle,
  onBulkToggleAuthor,
  onBulkDelete,
  onBulkApply,
  onBulkTape,
  onBulkFontSize,
  onBulkColorReset,
  onBulkColorPreview,
  onBulkFontPreview,
}) => {
  const zoom = useStore(state => state.transform[2])
  /*
    Auch die gemeinsame Leiste weicht der Geste — und hier wiegt es schwerer
    als am einzelnen Zettel: Beim Ziehen einer Mehrfachauswahl rechnet der
    Rahmen in jedem Frame neu, und mit ihm rendert die Leiste. Deren
    Ausweichrechnung misst dabei jedes Mal das Layout durch
    (`getBoundingClientRect` nach einem Stilschreiben), was den Zug spürbar
    zäh macht.
  */
  const pointerBusy = useCanvasPointerBusy(entries.length >= 2)
  const frameRef = useRef<HTMLDivElement>(null)

  const rects = entries.map(entry => entry.rect)
  /*
    Während eines **Platz-Tauschs** gilt der eingefrorene Rahmen.

    Ein Tausch bewegt nur INNERHALB der Auswahl: Dieselben Plätze sind vorher
    und nachher belegt, das umschließende Rechteck ist am Ende auf den Pixel
    dasselbe. Ein Rahmen, der währenddessen aufbläht (das gezogene Element
    hängt ja am Zeiger) und dann punktgenau zurückschnappt, hätte nichts
    erzählt — er hätte gezappelt.

    Beim Verschieben und Skalieren geht er weiterhin mit: Dort ändert sich die
    Ausdehnung wirklich.
  */
  // VOR dem frühen `return` unten — Hooks müssen in jedem Render laufen.
  const showBar = !pointerBusy
    && notes !== undefined && notes.length >= 2
    && Boolean(onBulkColor && onBulkFont && onBulkDelete)
  // Sanft raus wie rein (siehe `usePresence`).
  const barPresence = usePresence(showBar)

  const kinds = new Set(notes?.map(note => note.kind))
  /*
    Die Ecken gibt es nur, solange sich ALLES in der Auswahl skalieren lässt.
    Eine **Notiz** kann das nicht — ihre Höhe kommt vom Text, ziehbar ist
    allein die Breite, und das an der Kante. Mit einer Notiz in der Auswahl
    hätten die Ecken nichts zu tun; sie fallen weg. Rahmen und Verschieben
    bleiben.
  */
  // Der **Text** skaliert an SEINEN Ecken die Schrift; im Rahmen einer Gruppe
  // gibt es das nicht — die Ecken fallen weg wie beim Memo.
  const resizable = !kinds.has('memo') && !kinds.has('text')
  const onlyText = kinds.size === 1 && kinds.has('text')
  /*
    Nur die Schnittmenge — was auf ALLE wirkt (CONTEXT.md, Options-Leiste).
    Ein **Bereich** in der Auswahl lässt Farbe und Löschen übrig (Grillen
    2026-09-04): Er hat keine Schrift, keinen Autor, und das Übernehmen ist
    für ihn tot — ein toter Knopf, den man sich erklären müsste, ist keiner.
    Sein Name bleibt ebenso draußen wie der Titel der Notiz in gemischter
    Gesellschaft. Die Farbe wirkt auf beides: auf den Zettel kräftig, auf den
    Bereich blass — dieselbe Palette, zwei Stärken.
  */
  /*
    Der **Klebestreifen** fehlt in der gemischten Auswahl MIT Bereich, aus
    demselben Grund wie das Übernehmen: Ein Bereich bekommt keinen Streifen,
    der Knopf wirkte also nur auf einen Teil der Auswahl — und ein toter Knopf,
    den man sich erklären müsste, ist keiner.
  */
  /*
    Nur **Texte**: ihre eigene Leiste — Schriftfarbe, Schrift, Größe. Ein Text
    zwischen Zetteln lässt Schrift und Löschen übrig: Seine Farbe ist Tinte,
    ihre Fläche — ein Knopf für beides färbte das eine blass oder das andere
    grell. Titel, Autor, Übernehmen und Streifen kennt er nicht.
  */
  const barButtons: CanvasOptionsBarButton[] = onlyText
    ? ['color', 'font', 'size', 'delete']
    : kinds.has('text')
    ? (kinds.has('area') ? ['delete'] : ['font', 'delete'])
    : kinds.has('area')
    ? ['color', 'delete']
    : [
      'color',
      'font',
      ...(kinds.size === 1 && kinds.has('memo') ? ['title' as const] : []),
      'author',
      'apply',
      'tape',
      'delete',
    ]

  const box = frozenBox ?? getBoundingBox(rects)

  /*
    ── **Den Fokus holen, sobald der Rahmen ERSCHEINT** ──────────────────────

    Sonst erreichen Entf und Escape das Canvas-Element nicht: Nach einem
    **Auswahlrahmen** fokussiert React Flow sein eigenes Rechteck, das wir
    ausblenden — der Fokus fällt damit auf `document.body`. Von dort steigt
    kein Tastendruck zum Canvas auf, und `useCanvasDeleteShortcut` horcht
    genau dort (am Element, nicht am Dokument — damit ein Entf in der
    **Aufgabenliste** nicht beides löscht).

    **Die Bedingung MUSS in den Abhängigkeiten stehen.** Bis 2026-09-06 stand
    hier `[]`, und das war falsch: Dieses Overlay wird UNBEDINGT gemountet und
    lebt so lange wie der Canvas. Der Effekt lief also genau einmal, beim
    Aufbau — da gab es den Rahmen noch gar nicht (`frameRef.current` war
    `null`), und wenn er später erschien, lief nie wieder etwas. Gemeldet als
    „bei Mehrfachauswahl geht Entf nicht"; nachgemessen lag der Fokus danach
    auf `BODY`.

    Als BOOLEAN und nicht über `box` oder `rects`: Die ändern sich in jedem
    Frame eines Zugs, und ein Effekt, der den Fokus jedes Mal neu holte,
    risse ihn aus allem, was ihn inzwischen bekommen hat.
  */
  const frameShown = Boolean(box) && rects.length >= 2
  useEffect(() => {
    if (frameShown) frameRef.current?.focus({ preventScroll: true })
  }, [frameShown])
  /*
    Der letzte Stand der Leiste — Lage, Zettel, Knöpfe. Schrumpft die Auswahl
    auf einen oder keinen (Klick daneben), fiele der ganze Rahmen im selben
    Render weg, und die Leiste wäre fort, bevor sie abblenden kann. Aus dem
    Schnappschuss steht sie noch die Abblende lang, wo sie war.
  */
  const lastBarRef = useRef<{ box: Bounds; notes: readonly CanvasNote[]; buttons: CanvasOptionsBarButton[] } | null>(null)
  if (showBar && box && notes) lastBarRef.current = { box, notes, buttons: barButtons }
  const snap = lastBarRef.current
  /*
    Zustand der Übernehmen-Pille aus der Auswahl: bereit, sobald EINER noch
    übernommen werden kann; sonst „übernommen", wenn alle es sind; sonst leer.
  */
  const applicableCount = snap ? snap.notes.filter(isConvertible).length : 0
  const applyState: CanvasOptionsBarApplyState = applicableCount > 0
    ? 'ready'
    : snap && snap.notes.length > 0 && snap.notes.every(isConverted) ? 'done' : 'empty'

  const bar = barPresence.mounted && snap && onBulkColor && onBulkFont && onBulkDelete ? (

    /*
      Über der Oberkante, mittig — dieselbe Lage, die `NodeToolbar` einem
      einzelnen Zettel gibt.

      **Gegen den Zoom mit 1/zoom und nicht mit `canvasCounterScale`:**
      Jene Funktion hält bei 1 an (herausgezoomt wachsen die Griffe, hinein
      gezoomt nicht mehr) — richtig für einen Punkt, falsch für eine
      Leiste: Sie trüge bei 200 % doppelt so große Schrift wie dieselbe
      Leiste an einem einzelnen Zettel.

      `translate` vor `scale`, beides als Einzeleigenschaft: Die 8px Abstand
      werden dadurch mitskaliert und stehen auf dem Bildschirm bei jedem
      Zoom gleich.
    */
    <div
      className="canvas-selection__options"
      style={{
        left: (snap.box.left + snap.box.right) / 2,
        top: snap.box.top,
        scale: String(1 / zoom),
      }}
    >
      <CanvasOptionsBar
        leaving={barPresence.leaving}
        buttons={snap.buttons}
        colorKeys={snap.notes.map(note => note.color)}
        fontKeys={snap.notes.map(note => note.font)}
        colorKind={onlyText ? 'ink' : 'surface'}
        fontSizes={onlyText ? snap.notes.map(canvasTextSizeOf) : undefined}
        onFontSizeChange={onBulkFontSize}
        onColorReset={onBulkColorReset}
        onColorPreview={onBulkColorPreview}
        onFontPreview={onBulkFontPreview}
        titleOn={snap.notes.every(note => note.title !== null)}
        authorOn={snap.notes.every(note => note.showAuthor)}
        onColorChange={onBulkColor}
        onFontChange={onBulkFont}
        onToggleTitle={onBulkToggleTitle}
        onToggleAuthor={onBulkToggleAuthor}
        onDelete={onBulkDelete}
        onApply={onBulkApply}
        onTape={onBulkTape}
        applyState={applyState}
        applyCount={applicableCount}
        // Der Fokus zurück auf den Rahmen: Die Kürzel am Canvas (Entf …)
        // brauchen ein `keydown`, das von innen nach außen bubbelt.
        onActionDone={() => frameRef.current?.focus({ preventScroll: true })}
      />
    </div>
  ) : null

  // Gegen den Zoom gerechnet — dieselbe Funktion, die `CanvasFlow` als
  // `--canvas-scale` an die einzelnen Notizen gibt. Zwei Formeln für dasselbe
  // liefen unweigerlich auseinander.
  const scale = canvasCounterScale(zoom)

  /*
    Die Tauschpunkte — je einer in der Mitte eines ausgewählten Elements, und
    zwar SCHON AB EINEM: Daran gezogen tauscht der einzelne Zettel mit jedem
    anderen im Ausschnitt, ohne dass man vorher eine Auswahl aufziehen müsste
    (CONTEXT.md, „Tauschpunkt").

    Geschwister des Rahmens und nicht seine Kinder: Sie sitzen an den
    EINZELNEN Elementen, deren Lage sich beim Tauschen ändert, während der
    Rahmen eingefroren steht. Als Kinder wären sie zudem in seinem
    `pointer-events: none` gefangen.
  */
  const ringEntries = (swapPoints ?? entries).filter(entry => entry.id !== editingId)
  const rings = ringEntries.map(({ id, rect: r, accent }) => (
    <div
      key={id}
      className={
        'canvas-swap-point'
        + (id === swapDraggedId ? ' canvas-swap-point--dragging' : '')
      }
      data-swap-point={id}
      style={{
        left: r.x + r.width / 2,
        top: r.y + r.height / 2,
        scale: String(scale),
        // Die Farbe seines Zettels — die SCSS liest sie (CONTEXT.md, Tauschpunkt).
        '--swap-accent': accent ?? getCanvasNoteRingColor(null),
        // Über dem getragenen Element — siehe `SWAP_RING_LIFTED_Z`.
        ...(id === swapDraggedId ? { zIndex: SWAP_RING_LIFTED_Z } : {}),
      } as React.CSSProperties}
      onPointerDown={event => onSwapStart(id, event)}
      onPointerEnter={() => onSwapPointEnter?.(id)}
      onPointerLeave={event => onSwapPointLeave?.(id, event)}
      onDoubleClick={event => {
        // Wie `handleOpenEditor` am Zettel: Ohne das zoomte React Flows
        // Doppelklick am ZoomPane mit hinein.
        event.stopPropagation()
        onSwapPointDoubleClick?.(id)
      }}
    />
  ))

  /*
    Die **Ableg-Flächen** — in Canvas-Koordinaten, die Bänder schon gegen den
    Zoom gerechnet (`swapZonesFor`). Getroffen heißt: derselbe Besitzer,
    dieselbe Art, dieselbe Achse. **Nichts davon ist zu sehen** — das Angebot
    zeigen die Zettel selbst, indem sie ausweichen (`getSpreadPlan`), und was
    geschieht, sagt die **Marke** darunter (`swapMark`). Die Flächen bleiben
    als Marken im Bild: Sie sagen dem Test und dem Auge des Entwicklers, wo
    gerechnet wird, und tragen ihre Art und Achse als Klasse.
  */
  const isHit = (zone: SwapZone) =>
    swapHit != null && swapHit.owner.id === zone.owner.id && swapHit.kind === zone.kind && swapHit.axis === zone.axis
  const zones = (swapZones ?? []).map(zone => (
    <div
      key={`${zone.owner.id}:${zone.kind}:${zone.axis ?? ''}`}
      className={`canvas-swap-zone canvas-swap-zone--${zone.kind}${zone.axis ? ` canvas-swap-zone--${zone.axis}` : ''}${isHit(zone) ? ' canvas-swap-zone--armed' : ''}`}
      style={{
        left: zone.rect.x, top: zone.rect.y, width: zone.rect.width, height: zone.rect.height,
      }}
    />
  ))

  /*
    Die **Fuge** — der Strich in der geöffneten Lücke.

    **Bildschirmstark, nicht canvasstark** (`1 / zoom`, wie die Ringe oben):
    Eine Marke, die beim Herauszoomen zum Haar wird und beim Hineinzoomen zum
    Balken, meldete nicht mehr dasselbe. `canvasCounterScale` wäre hier falsch
    — die hält bei 1 an, richtig für einen Griff, den man treffen muss, falsch
    für einen Strich, den man nur sehen soll.

    Die LÄNGE dagegen steht in Canvas-Einheiten (`seamOf`): Sie misst sich an
    der Lücke, über der die Linie liegt, nicht am Bildschirm.
  */
  const seamStroke = 2 / zoom
  const horizontalRow = swapSeam?.seam.axis === 'x'
  const mark = swapSeam == null ? null : (
    <div
      className={`canvas-swap-mark canvas-swap-mark--seam canvas-swap-mark--${swapSeam.seam.axis}${swapSeam.on ? ' canvas-swap-mark--on' : ''}`}
      style={{
        left: horizontalRow ? swapSeam.seam.x - seamStroke / 2 : swapSeam.seam.x,
        top: horizontalRow ? swapSeam.seam.y : swapSeam.seam.y - seamStroke / 2,
        width: horizontalRow ? seamStroke : swapSeam.seam.length,
        height: horizontalRow ? swapSeam.seam.length : seamStroke,
        '--swap-mark-accent': swapSeam.accent,
        '--swap-mark-stroke': `${seamStroke}px`,
        '--swap-mark-cap': `${22 / zoom}px`,
      } as React.CSSProperties}
    >
      {/* Der Strich in eigener Hülle: Die äußere blendet, der innere atmet. */}
      <span className="canvas-swap-mark__stem" />
    </div>
  )

  /*
    Die **Zielfläche** (CONTEXT.md, Tauschpunkt): ein Hauch Fläche in den
    Maßen des Getragenen, dort, wo er beim Loslassen liegt — dieselbe Sprache
    wie die **Ableg-Fläche** der **Werkzeug-Pille**. **Umriss** und **Fuge**
    beantworten andere Fragen (wer macht Platz, wo geht die Reihe auf); über
    einem **Leerplatz** sagte bisher gar nichts, wohin der Zettel springt.
  */
  const landing = swapLanding ? (
    <div
      className="canvas-landing"
      style={{
        left: swapLanding.x, top: swapLanding.y,
        width: swapLanding.width, height: swapLanding.height,
      }}
      aria-hidden="true"
    />
  ) : null

  // Der Rahmen erst ab zwei: bei einer greift der vorhandene Weg über
  // `NodeResizeControl` an der Notiz selbst — der Tauschpunkt aber steht.
  // Die Leiste darf dabei noch abblenden (siehe `snap`).
  if (!box || rects.length < 2) {
    return (
      <ViewportPortal>
        {bar}
        {landing}
        {zones}
        {mark}
        {rings}
      </ViewportPortal>
    )
  }

  /*
    Die gemeinsame **Options-Leiste**: ab zwei Elementen gehört sie dem Rahmen,
    und die Leisten der einzelnen weichen (CONTEXT.md, „Auswahlrahmen").

    **Nur die Schnittmenge:** Der Titel-Umschalter gehört der **Notiz** — ist
    ein **Notiz** dabei, fällt er weg. Die Leiste zeigt damit ausschließlich,
    was auf ALLE wirkt.
  */

  return (
    <ViewportPortal>
      {bar}
      {landing}
      {zones}
      {mark}

      {/*
        Die Fläche liegt UNTER den Notizen (siehe SCSS): Läge sie darüber,
        finge sie deren Klicks ab — „Klick in der Auswahl behält sie" und der
        Doppelklick zum Bearbeiten wären damit tot.

        Ein Klick ohne Ziehen erreicht die Pane dadurch nicht und hebt die
        Auswahl also nicht auf. Auch das ist Miros Verhalten.
      */}
      <div
        className="canvas-selection__surface"
        style={{
          left: box.left,
          top: box.top,
          width: box.right - box.left,
          height: box.bottom - box.top,
        }}
        onPointerDown={onMoveStart}
      />

      <div
        ref={frameRef}
        tabIndex={-1}
        className="canvas-selection__frame"
        style={{
          left: box.left,
          top: box.top,
          width: box.right - box.left,
          height: box.bottom - box.top,
          // Der Rahmen selbst wird ebenfalls gegen den Zoom gerechnet, sonst
          // wäre er herausgezoomt ein Balken.
          '--selection-scale': scale,
          // Seine Stärke gibt beim Herauszoomen ein wenig nach — die Begründung
          // steht bei `selectionStrokeWidth`. DIESELBE Zahl trägt der Rahmen
          // der einzelnen Notiz (dort als `--canvas-frame-width` am
          // Canvas-Element), sonst sähe eine ausgewählte Notiz anders aus als
          // dieselbe Notiz in einer Mehrfachauswahl.
          '--selection-frame-width': `${selectionStrokeWidth(zoom)}px`,
        } as React.CSSProperties}
      />

      {/*
        Die Ziehpunkte — Geschwister des Rahmens, nicht seine Kinder.

        **Bedienelemente liegen über der Rückmeldung** (CONTEXT.md): Der Rahmen
        ist Rückmeldung und teilt sich mit **Hilfslinie** und **Maßklammer** die
        2000; die Griffe gehören darüber, wie der **Tauschpunkt** daneben schon
        liegt.

        Als Kinder ging das nicht: Der Rahmen ist mit seinem `z-index` selbst
        ein Stapelkontext, alles in ihm wäre auf 2000 gedeckelt — und bei
        Gleichstand gewinnt die später gerenderte Hilfslinie. Die Punkte lagen
        dadurch unter ihr und sahen aus, als wären sie durchgeschnitten.

        Die Lage kommt jetzt aus `box` statt aus `left: 0 / 100%` am Rahmen —
        dieselben vier Zahlen, die der Rahmen ohnehin benutzt.
      */}
      {resizable && CORNERS.map(corner => (
        <div
          key={corner}
          className={`canvas-selection__handle canvas-selection__handle--${corner}`}
          style={{
            left: corner.endsWith('left') ? box.left : box.right,
            top: corner.startsWith('top') ? box.top : box.bottom,
            scale: String(scale),
          }}
          onPointerDown={event => onResizeStart(corner, event)}
        />
      ))}

      {rings}
    </ViewportPortal>
  )
}
